import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { adminService } from '../../../services/adminService';
import { getCanonicalPricingTiers } from '../../../config/pricingTiers';
import { isRecognizedPlanKey, normalizePlanKey } from '../../../config/planFeatures';

const accountPlanValue = (account) => {
  const savedValue = String(account?.planType || account?.tier || '').trim();
  return savedValue && !isRecognizedPlanKey(savedValue)
    ? savedValue
    : normalizePlanKey(savedValue);
};

const AccountDetailModal = ({ account, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    planType: accountPlanValue(account),
    subscriptionStatus: account?.subscriptionStatus || 'active',
    companyName: account?.companyName || '',
  });
  const [planChanged, setPlanChanged] = useState(false);
  const [featureFlags, setFeatureFlags] = useState({});
  const [editedFeatureFlags, setEditedFeatureFlags] = useState({});
  const [accountForms, setAccountForms] = useState([]);
  const [accountSubmissions, setAccountSubmissions] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const canonicalPricingTiers = getCanonicalPricingTiers(pricingTiers);

  // Load live pricing tiers so plan options always match Pricing & Plans Management
  useEffect(() => {
    adminService?.getAllPricingTiersAdmin?.()
      .then((tiers) => { if (tiers?.length) setPricingTiers(tiers); })
      .catch(() =>
        adminService?.getAllPricingTiers?.()
          .then((tiers) => { if (tiers?.length) setPricingTiers(tiers); })
          .catch(() => {})
      );
  }, []);

  // Sync formData when account prop changes (after parent updates selectedAccount)
  useEffect(() => {
    if (account) {
      setFormData({
        planType: accountPlanValue(account),
        subscriptionStatus: account?.subscriptionStatus || 'active',
        companyName: account?.companyName || '',
      });
      setPlanChanged(false);
      setFeatureFlags({
        ...(typeof account?.accountFeatures?.white_label === 'boolean'
          ? { white_label: account.accountFeatures.white_label }
          : {}),
        ...(typeof account?.accountFeatures?.client_workspaces === 'boolean'
          ? { client_workspaces: account.accountFeatures.client_workspaces }
          : {}),
      });
      setEditedFeatureFlags({});
    }
  }, [account?.id, account?.planType, account?.subscriptionStatus, account?.companyName, account?.accountFeatures]);

  useEffect(() => {
    if (activeTab === 'usage') {
      loadUsageData();
    }
  }, [activeTab]);

  const loadUsageData = async () => {
    setUsageLoading(true);
    try {
      const [forms, submissions] = await Promise.all([
        adminService?.getAccountForms(account?.id),
        adminService?.getAccountSubmissions(account?.id),
      ]);
      setAccountForms(forms || []);
      setAccountSubmissions(submissions || []);
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setSaveError('');
    setSaveSuccess(false);

    try {
      const changedFlags = Object.keys(editedFeatureFlags).reduce((overrides, key) => ({
        ...overrides,
        [key]: featureFlags[key],
      }), {});
      const updates = {
        subscriptionStatus: formData?.subscriptionStatus,
        companyName: formData?.companyName?.trim() || null,
      };
      if (planChanged) updates.planType = formData?.planType;
      if (Object.keys(changedFlags).length > 0) updates.featureFlags = changedFlags;

      await onUpdate(account?.id, updates);
      setSaveSuccess(true);
      // Keep modal open briefly to show success, then close
      setTimeout(() => {
        setSaveSuccess(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error updating account:', error);
      setSaveError(error?.message || 'Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const setFeatureOverride = (featureKey, enabled) => {
    setFeatureFlags((current) => ({ ...current, [featureKey]: enabled }));
    setEditedFeatureFlags((current) => ({ ...current, [featureKey]: true }));
  };

  const featureOverrideLabel = (featureKey) => (
    Object.prototype.hasOwnProperty.call(featureFlags, featureKey)
      ? `Account override: ${featureFlags[featureKey] ? 'enabled' : 'disabled'}`
      : 'Uses plan default'
  );
  const planOptions = [
    ...(!isRecognizedPlanKey(formData?.planType)
      ? [{ value: formData.planType, label: `Unsupported saved value: ${formData.planType}` }]
      : []),
    ...canonicalPricingTiers.map((tier) => ({
      value: tier.planKey,
      label: tier.tierName,
    })),
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'billing', label: 'Billing', icon: 'CreditCard' },
    { id: 'usage', label: 'Usage', icon: 'BarChart3' },
  ];

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-xl font-heading font-semibold text-foreground">
              Account Details
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {account?.email || account?.tier || 'User Account'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-6 pt-4 border-b border-border">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-md font-medium text-sm transition-smooth ${
                activeTab === tab?.id
                  ? 'bg-primary/10 text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted/30'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* ── Overview Tab ── */}
          {activeTab === 'overview' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Account ID
                  </label>
                  <p className="text-sm text-muted-foreground font-mono bg-muted/30 p-2 rounded break-all">
                    {account?.id}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  <p className="text-sm text-muted-foreground bg-muted/30 p-2 rounded">
                    {account?.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name
                  </label>
                  <p className="text-sm text-muted-foreground bg-muted/30 p-2 rounded">
                    {account?.fullName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Created At
                  </label>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(account?.createdAt)}
                  </p>
                </div>
              </div>

              <Input
                label="Company Name"
                value={formData?.companyName}
                onChange={(e) =>
                  setFormData({ ...formData, companyName: e?.target?.value })
                }
                placeholder="Enter company name"
              />

              <Select
                label="Plan Type"
                value={formData?.planType}
                onChange={(planType) => {
                  setFormData({ ...formData, planType });
                  setPlanChanged(true);
                }}
                options={planOptions}
              />
              {!isRecognizedPlanKey(formData?.planType) && (
                <p className="text-xs text-warning">
                  This account has an unsupported saved plan. Select an approved plan before changing its plan assignment.
                </p>
              )}

              <Select
                label="Subscription Status"
                value={formData?.subscriptionStatus}
                onChange={(subscriptionStatus) => setFormData({ ...formData, subscriptionStatus })}
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' },
                  { value: 'suspended', label: 'Suspended' },
                ]}
              />

              {/* Premium Feature Access */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Premium Feature Access
                  </label>
                  <p className="text-xs text-muted-foreground">
                    Enable for paid accounts with more than 20 forms, or as an add-on.
                  </p>
                </div>
                <div className="border border-border rounded-lg divide-y divide-border">
                  <div className="flex items-start gap-3 p-3">
                    <input
                      type="checkbox"
                      id="acc_white_label"
                      checked={featureFlags?.white_label === true}
                      onChange={(e) => setFeatureOverride('white_label', e?.target?.checked)}
                      className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <label htmlFor="acc_white_label" className="cursor-pointer flex-1">
                      <span className="block text-sm font-medium text-foreground">White-Label Branding</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Custom domain, logo, colors, and agency branding settings. {featureOverrideLabel('white_label')}
                      </span>
                    </label>
                  </div>
                  <div className="flex items-start gap-3 p-3">
                    <input
                      type="checkbox"
                      id="acc_client_workspaces"
                      checked={featureFlags?.client_workspaces === true}
                      onChange={(e) => setFeatureOverride('client_workspaces', e?.target?.checked)}
                      className="mt-0.5 w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <label htmlFor="acc_client_workspaces" className="cursor-pointer flex-1">
                      <span className="block text-sm font-medium text-foreground">Client Workspaces</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">
                        Isolated client workspaces with separate branding and form access. {featureOverrideLabel('client_workspaces')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {saveError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">{saveError}</p>
                </div>
              )}

              {saveSuccess && (
                <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-md">
                  <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
                  <p className="text-sm text-success font-medium">Account updated successfully!</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" loading={loading} disabled={loading}>
                  Save Changes
                </Button>
              </div>
            </form>
          )}

          {/* ── Billing Tab ── */}
          {activeTab === 'billing' && (
            <div className="space-y-6">

              {/* Current plan summary */}
              <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">Current Plan</p>
                  <p className="text-xl font-bold text-foreground capitalize">
                     {canonicalPricingTiers.find((tier) => tier.planKey === formData.planType)?.tierName || formData.planType || 'Free'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Status: <span className="font-medium capitalize text-foreground">{formData.subscriptionStatus || 'active'}</span>
                  </p>
                </div>
                <div className="p-3 bg-primary/10 rounded-full">
                  <Icon name="CreditCard" size={24} className="text-primary" />
                </div>
              </div>

              {/* Plan picker — upgrade / downgrade (live from pricing_tiers) */}
              <div>
                <p className="text-sm font-semibold text-foreground mb-3">Change Plan</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                   {canonicalPricingTiers.map((tier, idx, arr) => {
                     const planKey    = tier.planKey;
                    const isCurrent  = formData.planType === planKey;
                     const currentIdx = arr.findIndex((candidate) => candidate.planKey === formData.planType);
                    const isUpgrade  = idx > currentIdx;
                    const isDowngrade = idx < currentIdx;
                    const fmtLim = (v) => v === -1 || v === undefined ? 'Unlimited' : v >= 1000 ? `${v/1000}K` : String(v ?? '?');

                    return (
                      <button
                        key={tier.id || planKey}
                        type="button"
                         onClick={() => {
                           if (isCurrent) return;
                           setFormData((current) => ({ ...current, planType: planKey }));
                           setPlanChanged(true);
                         }}
                        className={`text-left p-4 rounded-lg border-2 transition-all ${
                          isCurrent
                            ? 'border-primary bg-primary/5 cursor-default'
                            : 'border-border hover:border-primary/40 hover:bg-muted/30 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <span className="font-semibold text-foreground text-sm">{tier.tierName}</span>
                            <span className="ml-2 text-xs text-muted-foreground">
                              {tier.price === null ? 'Custom' : tier.price === 0 ? 'Free' : `$${tier.price}/mo`}
                            </span>
                          </div>
                          {isCurrent && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                              <Icon name="Check" size={10} /> Current
                            </span>
                          )}
                          {!isCurrent && isUpgrade && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">
                              <Icon name="ArrowUp" size={10} /> Upgrade
                            </span>
                          )}
                          {!isCurrent && isDowngrade && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-warning bg-warning/10 px-2 py-0.5 rounded-full">
                              <Icon name="ArrowDown" size={10} /> Downgrade
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Icon name="FileText" size={10} />{fmtLim(tier.maxForms)} forms</span>
                          <span className="flex items-center gap-1"><Icon name="Inbox" size={10} />{fmtLim(tier.maxSubmissions)}/mo</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div>
                <p className="text-sm font-semibold text-foreground mb-2">Subscription Status</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { value: 'active',    label: 'Active',    icon: 'CheckCircle2', color: 'success' },
                    { value: 'inactive',  label: 'Inactive',  icon: 'MinusCircle',  color: 'muted' },
                    { value: 'suspended', label: 'Suspended', icon: 'XCircle',      color: 'destructive' },
                  ].map((s) => {
                    const isSel = formData.subscriptionStatus === s.value;
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, subscriptionStatus: s.value }))}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                          isSel
                            ? `border-${s.color} bg-${s.color}/10 text-${s.color}`
                            : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        <Icon name={s.icon} size={14} />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {saveError && (
                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive">{saveError}</p>
                </div>
              )}
              {saveSuccess && (
                <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-md">
                  <Icon name="CheckCircle2" size={16} className="text-success flex-shrink-0" />
                  <p className="text-sm text-success font-medium">Plan updated successfully!</p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
                <Button onClick={handleSubmit} loading={loading} disabled={loading}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}

          {/* ── Usage Tab ── */}
          {activeTab === 'usage' && (
            <div className="space-y-6">
              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={28} className="animate-spin text-primary" />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-muted/30 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Total Forms</p>
                      <p className="text-2xl font-bold text-foreground">
                        {accountForms?.length}
                      </p>
                    </div>
                    <div className="bg-muted/30 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Total Submissions</p>
                      <p className="text-2xl font-bold text-foreground">
                        {accountSubmissions?.length}
                      </p>
                    </div>
                    <div className="bg-muted/30 p-4 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Last Login</p>
                      <p className="text-sm font-medium text-foreground">
                        {account?.lastLoginAt ? formatDate(account?.lastLoginAt) : 'Never'}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-foreground mb-3">Recent Forms</h4>
                    {accountForms?.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Icon name="FileText" size={32} className="mx-auto mb-2 opacity-50" />
                        <p>No forms created yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {accountForms?.slice(0, 5)?.map((form) => (
                          <div
                            key={form?.id}
                            className="flex items-center justify-between p-3 bg-muted/30 rounded-md"
                          >
                            <div>
                              <p className="font-medium text-foreground">
                                {form?.name || form?.title}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {form?.submissionsCount || 0} submissions
                              </p>
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {formatDate(form?.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetailModal;